import React, { useState } from 'react';
import { Switch } from '@nearst/ui';
import { Protect } from '@clerk/clerk-react';
import Tippy from '@tippyjs/react';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import * as Sentry from '@sentry/browser';

import { useShop } from '@services/ShopProvider';
import { setHideUnenrichedProducts } from '@services/shop';
import AuthorizationTooltip from '@components/AuthorizationTooltip';
import { useCheckPermission } from '@services/auth/hooks';

import styles from './UnenrichedProductsToggle.module.scss';

const TOOLTIP_TEXT =
	'Products that only have a barcode (no title, description, or images) are hidden from your storefront and feed channels (Pinterest, TikTok, Snap). Google Local Listings is not affected — Google supplements its own product data. These products stay visible here so you can add titles.';

export const UnenrichedProductsToggle = () => {
	const { shop, data: shops = [], mutate } = useShop();
	const authorized = useCheckPermission('org:inventory:edit');
	const [pending, setPending] = useState(false);

	// The chain-wide inventory view (/inventory/items) has no shop in context, so the
	// toggle reflects — and writes — every shop in the organisation. A single shop's
	// inventory (/inventory/:shopId/items) reflects just that shop. Default ON: only an
	// explicit `false` on a shop opts it out.
	const isOrganisationLevel = !shop?.id;
	const relevantShops = isOrganisationLevel ? shops : [shop];
	const hiding = relevantShops.every((s) => s?.hideUnenrichedProducts !== false);

	const onChange = async (e) => {
		const hideUnenrichedProducts = e.target.checked;
		setPending(true);
		try {
			// shop?.id is undefined on the chain view → the backend applies to all shops in the org
			await setHideUnenrichedProducts(shop?.id, hideUnenrichedProducts);
			await mutate();
		} catch (err) {
			Sentry.captureException(err);
			alert("Something went wrong and we couldn't update your details.\nPlease try again.");
		} finally {
			setPending(false);
		}
	};

	return (
		<Protect permission="org:inventory:edit" fallback={null}>
			<AuthorizationTooltip authorized={authorized}>
				<label className={styles.toggle}>
					<Switch checked={hiding} onChange={onChange} disabled={!authorized || pending} />
					<span>Hide products without titles</span>
				</label>
			</AuthorizationTooltip>
			<Tippy content={TOOLTIP_TEXT} placement="bottom" className={styles.tooltip}>
				<InformationCircleIcon className={styles.infoIcon} />
			</Tippy>
		</Protect>
	);
};
